// Shared types for the Firmware Hub Download tab.

import { INSTANCE } from '../instance';

export type FirmwareCategory = 'px4' | 'wifi' | 'lora' | 'drone_vision' | 'scanner_shell';

export interface FirmwareModel {
  key: string;   // stable identifier stored in applicable_models[]
  name: string;  // display name
}

// Architecture preset: short filename token <-> full architecture string.
// The server is the single source (ARCH_PRESETS); the FE renders the datalist
// and auto-detects from it.
export interface ArchPreset {
  token: string; // e.g. 'ark'  (lands in the filename)
  arch: string;  // e.g. 'ark_pi6x_default'  (stored in the architecture field)
}

// One target file under a build. A single commit built for two boards is TWO of
// these on ONE Firmware — that's what `architecture` distinguishes and what
// `sha256` identifies. Each may carry an optional detached signature (.sig).
export interface FirmwareFile {
  id: number;
  firmware_id: number;
  architecture: string | null;
  applicable_models: string[] | null;
  file_name: string;
  file_size_bytes: number | string | null; // pg BIGINT arrives as string
  mime_type: string | null;
  sha256: string;
  sig_file_name: string | null;
  sig_file_size_bytes: number | string | null;
  sig_sha256: string | null;
  has_signature: boolean;
  sig_uploaded_at: string | null;
  sig_uploaded_by_name: string | null;
  uploaded_at: string | null;
  uploaded_by_name: string | null;
}

// A build = one commit. One card on the Download tab, one /firmware/:hash page.
export interface Firmware {
  id: number;
  category: FirmwareCategory;
  label: string;
  version: string | null;
  version_name?: string | null; // scanner_shell only: the APK's versionName (version holds its versionCode)
  git_hash: string | null;
  branch: string | null;
  notes: string | null;
  is_active: boolean;
  is_archived: boolean;
  release_date: string | null;
  uploaded_at: string | null;
  archived_at: string | null;
  uploaded_by_name: string | null;
  archived_by_name: string | null;
  comment_count?: number; // # of comments on this build (from the list payload)

  // Every target file, oldest first.
  files: FirmwareFile[];
  // Union of applicable_models across files — what the card-level model filter reads.
  applicable_models: string[] | null;

  // The PRIMARY (oldest) file's identity, flattened onto the build by the server.
  // Convenient for single-target builds and for the shareable-link short hash;
  // on a multi-target build these describe only files[0], so render per-file
  // details from `files`, not from here.
  primary_file_id: number | null;
  sha256: string | null;
  file_name: string | null;
  file_size_bytes: number | string | null;
  mime_type: string | null;
  architecture: string | null;
}

// One comment in a firmware's append-only thread. Same shape the event/tour
// threads use, so the rendering stays familiar.
export interface FirmwareMessage {
  id: number;
  user_id: string;
  message_text: string;
  created_at: string;
  firstname: string | null;
  lastname: string | null;
}

export interface FirmwareListResponse {
  models: FirmwareModel[];
  archPresets: ArchPreset[];
  // Keys are the instance's categories (the server groups by its own allowlist),
  // so a category this deployment does not offer is absent, not empty.
  categories: Partial<Record<FirmwareCategory, Firmware[]>>;
  attachable: Firmware[];
}

// Which categories this deployment offers, in render order. Mirrors the server's
// categoriesFor(instance): drone_vision is the SwarmSystems companion-computer
// bundle and Firefleet never sees it (allowlist, per two-instances.md).
// scanner_shell is admin-only: the server leaves it out of a non-admin's payload,
// and the Download tab renders only the categories the payload carries.
export const CATEGORY_ORDER: FirmwareCategory[] =
  INSTANCE === 'swarm'
    ? ['px4', 'wifi', 'lora', 'drone_vision', 'scanner_shell']
    : ['px4', 'wifi', 'lora', 'scanner_shell'];

export const CATEGORY_LABELS: Record<FirmwareCategory, string> = {
  px4: 'PX4 Firmware',
  wifi: 'WiFi Firmware',
  lora: 'LoRa Firmware',
  drone_vision: 'Drone Vision',
  scanner_shell: 'Scanner app',
};

// URL slug per category — mirrors the server's CATEGORY_SLUGS. px4/wifi/lora are
// their own slug; only drone_vision pages are namespaced (/firmware/dronevis/:hash).
// The bare /firmware/:hash page stays the universal resolver.
export const CATEGORY_SLUGS: Record<FirmwareCategory, string> = {
  px4: 'px4',
  wifi: 'wifi',
  lora: 'lora',
  drone_vision: 'dronevis',
  scanner_shell: 'scanner',
};

// A build's shareable page: namespaced for drone_vision, bare for everything else
// (existing links and bookmarks keep working unchanged).
export function firmwarePath(category: FirmwareCategory, hash: string): string {
  return category === 'drone_vision' ? `/firmware/${CATEGORY_SLUGS[category]}/${hash}` : `/firmware/${hash}`;
}
